import {CustomModal} from '_metronic/partials/modals/CustomModal'
import {useAuth} from 'app/modules/auth'
import {restrictedDomains} from 'app/modules/auth/core/restrictedDomain'
import {FC, useState} from 'react'
import * as Yup from 'yup'
import Input from 'app/components/Input'
import {useFormik} from 'formik'
import {addDoc, collection, serverTimestamp, Timestamp, updateDoc} from '@firebase/firestore'
import db from 'services/firebase'
import {showSuccessToast} from 'app/components/ShowToast'

type Props = {
  isModalOpen: boolean
  hideModal: () => void
}

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .min(3, 'Minimum 8 characters')
    .max(50, 'Maximum 50 characters')
    .required('Email is required')
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Please enter a valid email address')
    .test('is-work-email', 'Please use your company work email', (value) => {
      const domain = value.split('@')[1]?.toLowerCase()
      if (!domain) return false
      return !restrictedDomains.includes(domain)
    }),
})

const AddSubscriberModal: FC<Props> = ({isModalOpen, hideModal}) => {
  const {currentUser} = useAuth()
  const [loading, setLoading] = useState(false)

  const formik = useFormik({
    initialValues: {email: ''},
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true)
      try {
        const tenantId = currentUser?.tenantId || ''
        const subscribersRef = collection(
          db,
          'tenants',
          tenantId,
          'trust_center',
          'subscribers',
          'all_subscribers'
        )

        const newDocRef = await addDoc(subscribersRef, {
          email: values.email,
          subscribedOn: Timestamp.fromDate(new Date()),
        })

        await updateDoc(newDocRef, {
          id: newDocRef.id,
        })

        showSuccessToast({
          message: 'New subscriber for trust center updates has been added successfully',
        })

        const currentDate = new Date()
        const year = currentDate.getFullYear()
        const month = currentDate.getMonth() + 1
        const day = currentDate.getDate()
        const todaysDate = month + '/' + day + '/' + year
        const activitiesRef = collection(db, 'tenants', tenantId, 'activities')
        await addDoc(activitiesRef, {
          message: `${values.email} subscriber email for trust center has been added`,
          timestamp: serverTimestamp(),
          tenantId: currentUser?.tenantId,
          uid: currentUser?.uid,
          username: currentUser?.userName,
          date: todaysDate,
          email: currentUser?.email,
          target: 'Trust Center',
          action: 'Subscriber added',
        })
      } catch (error) {
        console.error('Error while adding subscriber for trust center')
      } finally {
        setLoading(false)
        hideModal()
      }
    },
  })

  const okBtnClickHandler = () => {
    formik.submitForm()
  }

  return (
    <CustomModal
      isOpen={isModalOpen}
      onClose={hideModal}
      title={'Add Subscriber'}
      isCloseIconShown={true}
      okBtnText='Add Subscriber'
      okBtnClickHandler={okBtnClickHandler}
      isLoading={loading}
      isLoadingBtnText='Adding Subscriber...'
    >
      <Input
        type='email'
        label='Email'
        autoComplete='off'
        placeholder='email@company.org'
        error={formik.touched.email && formik.errors.email ? formik.errors.email : ''}
        {...formik.getFieldProps('email')}
      />
    </CustomModal>
  )
}

export default AddSubscriberModal
